import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { WorkflowStageEditor } from './WorkflowStageEditor'
import { ApproverSelector } from './ApproverSelector'
import type {
  ApprovalWorkflowDetail,
  ApprovalStage,
  CreateApprovalStageRequest,
  AddStageApproverRequest,
} from '@/lib/api/types'
import { Plus, ArrowLeft, Loader2, Save } from 'lucide-react'
import { cn } from '@/lib/utils'

interface WorkflowBuilderProps {
  workflow: ApprovalWorkflowDetail
  onBack: () => void
  onSave: (name: string, description: string | undefined) => Promise<void>
  onAddStage: (stage: CreateApprovalStageRequest) => Promise<void>
  onUpdateStage: (stageId: string, updates: Partial<ApprovalStage>) => Promise<void>
  onRemoveStage: (stageId: string) => Promise<void>
  onAddApprover: (stageId: string, approver: AddStageApproverRequest) => Promise<void>
  onRemoveApprover: (stageId: string, approverId: string) => Promise<void>
  saving?: boolean
}

export function WorkflowBuilder({
  workflow,
  onBack,
  onSave,
  onAddStage,
  onUpdateStage,
  onRemoveStage,
  onAddApprover,
  onRemoveApprover,
  saving,
}: WorkflowBuilderProps) {
  const [name, setName] = useState(workflow.name)
  const [description, setDescription] = useState(workflow.description || '')
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set())
  const [approverSelectorOpen, setApproverSelectorOpen] = useState(false)
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null)
  const [addingStage, setAddingStage] = useState(false)

  const toggleStageExpanded = (stageId: string) => {
    setExpandedStages((prev) => {
      const next = new Set(prev)
      if (next.has(stageId)) {
        next.delete(stageId)
      } else {
        next.add(stageId)
      }
      return next
    })
  }

  const handleAddStage = async () => {
    setAddingStage(true)
    try {
      const newStage: CreateApprovalStageRequest = {
        name: `Stage ${workflow.stages.length + 1}`,
        order: workflow.stages.length + 1,
        requiredApprovals: 1,
        autoApproveMinorChanges: false,
        notifyOnSubmit: true,
        approvers: [],
      }
      await onAddStage(newStage)
    } finally {
      setAddingStage(false)
    }
  }

  const handleAddApproverClick = (stageId: string) => {
    setSelectedStageId(stageId)
    setApproverSelectorOpen(true)
  }

  const handleApproverSelected = async (
    approver: AddStageApproverRequest & { approverName: string }
  ) => {
    if (!selectedStageId) return

    const { approverName: _, ...approverData } = approver
    await onAddApprover(selectedStageId, approverData)
    setApproverSelectorOpen(false)
    setSelectedStageId(null)
  }

  const handleSave = async () => {
    await onSave(name, description || undefined)
  }

  const sortedStages = [...workflow.stages].sort((a, b) => a.order - b.order)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              Edit Workflow
            </h1>
            <p className="text-neutral-500 dark:text-neutral-400 mt-0.5">
              Configure stages and approvers
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving || !name.trim()}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Workflow Details */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="workflow-name">Workflow Name</Label>
          <Input
            id="workflow-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Standard Approval Process"
          />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="workflow-description">Description (optional)</Label>
          <Textarea
            id="workflow-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the purpose and usage of this workflow..."
            rows={2}
          />
        </div>
      </div>

      {/* Stages */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              Approval Stages
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Define the stages templates must pass through before approval
            </p>
          </div>
          <Button onClick={handleAddStage} disabled={addingStage}>
            {addingStage ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Add Stage
          </Button>
        </div>

        {sortedStages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg">
            <div className="p-4 rounded-full bg-neutral-100 dark:bg-neutral-800 mb-4">
              <Plus className="h-8 w-8 text-neutral-400" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
              No stages defined
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-4">
              Add stages to define your approval workflow. Each stage can have multiple approvers.
            </p>
            <Button onClick={handleAddStage} disabled={addingStage}>
              {addingStage ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add First Stage
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedStages.map((stage, index) => (
              <div key={stage.id} className="relative">
                {/* Connector Line */}
                {index < sortedStages.length - 1 && (
                  <div
                    className={cn(
                      'absolute left-[30px] top-full h-3 w-0.5 bg-neutral-200 dark:bg-neutral-700',
                      expandedStages.has(stage.id) ? 'hidden' : 'block'
                    )}
                  />
                )}
                <WorkflowStageEditor
                  stage={stage}
                  isExpanded={expandedStages.has(stage.id)}
                  onToggleExpand={() => toggleStageExpanded(stage.id)}
                  onUpdate={(updates) => onUpdateStage(stage.id, updates)}
                  onDelete={() => onRemoveStage(stage.id)}
                  onAddApprover={() => handleAddApproverClick(stage.id)}
                  onRemoveApprover={(approverId) => onRemoveApprover(stage.id, approverId)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approver Selector Dialog */}
      <ApproverSelector
        open={approverSelectorOpen}
        onOpenChange={setApproverSelectorOpen}
        onSelect={handleApproverSelected}
        existingApproverIds={
          selectedStageId
            ? workflow.stages
                .find((s) => s.id === selectedStageId)
                ?.approvers.map((a) => a.approverId) || []
            : []
        }
      />
    </div>
  )
}
